import { useContext, useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PlannerContext } from "../../../../context/planner/PlannerContext";
import {
  TimeRangeFields,
  isTimeRangeValid,
  DeleteBlockSection,
  SaveBlockButton,
} from "./shared";
import { ColourPicker } from "../../../pickers";
import { PICKER_COLORS } from "../../../../constants/pickerColors";

const EditTimeBlock = ({ block }) => {
  const {
    updateBlock,
    deleteBlock,
    setSelectedBlockId,
    setSelectedTool,
    closeToolPanel,
  } = useContext(PlannerContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [name, setName] = useState(block.name || "");
  const [startTime, setStartTime] = useState(block.startTime);
  const [endTime, setEndTime] = useState(block.endTime);
  const [color, setColor] = useState(block.color || null);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when block changes
  useEffect(() => {
    setName(block.name || "");
    setStartTime(block.startTime);
    setEndTime(block.endTime);
    setColor(block.color || null);
    setHasChanges(false);
  }, [block.id, block.name, block.startTime, block.endTime, block.color]);

  // Track changes
  useEffect(() => {
    const changed =
      name !== (block.name || "") ||
      startTime !== block.startTime ||
      endTime !== block.endTime ||
      color !== (block.color || null);
    setHasChanges(changed);
  }, [name, startTime, endTime, color, block]);

  const handleSave = () => {
    updateBlock(block.id, { name, startTime, endTime, color });
    setHasChanges(false);
    setSelectedBlockId(null);
    setSelectedTool("dayConfig");
    if (isMobile) {
      closeToolPanel();
    }
  };

  const handleDelete = () => {
    deleteBlock(block.id);
    if (isMobile) {
      closeToolPanel();
    }
  };

  const timesValid = isTimeRangeValid(startTime, endTime);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
        mt: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          pb: 2,
        }}
      >
        <Box sx={{ py: 2 }}>
          <TextField
            label="Block Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              Colour
            </Typography>
            <ColourPicker
              selectedColor={color || "#2196f3"}
              onColorSelect={setColor}
              colors={PICKER_COLORS}
              size={32}
            />
          </Box>

          <TimeRangeFields
            startTime={startTime}
            endTime={endTime}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
          />

          {!isMobile && (
            <>
              <SaveBlockButton
                onSave={handleSave}
                disabled={!hasChanges || !timesValid}
              />

              <DeleteBlockSection onDelete={handleDelete} showLabel={false} />
            </>
          )}
        </Box>
      </Box>

      {isMobile && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <SaveBlockButton
            onSave={handleSave}
            disabled={!hasChanges || !timesValid}
          />
          <DeleteBlockSection
            onDelete={handleDelete}
            showDivider={false}
            showLabel={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default EditTimeBlock;
